import {ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {MatPaginator} from '@angular/material/paginator';

import {MatTableDataSource} from '@angular/material/table';
import {BehaviorSubject, merge} from 'rxjs';
import {SubSink} from 'subsink';
import {debounceTime} from 'rxjs/operators';
import {MultishareHistoryFacadeService} from '../multishare-history-facade.service';
import {Share} from '../../domain';

export interface MultishareHistoryFilter {
  performedBy?: string;
  dateFrom?: Date;
  dateTo?: Date;
  page: {
    page: number,
    size: number
  }
}

export interface MultishareHistoryFilterModel {
  performedBy?: string;
  dateFrom?: Date;
  dateTo?: Date;
}

@Component({
  selector: 'app-multishare-history-table',
  template: `
    <div class="table-handling">
      <mat-paginator [pageSizeOptions]="[5,25,50,100]"
                     [pageIndex]="defaultPageIndex" [pageSize]="defaultPageSize"></mat-paginator>
    </div>
    <mat-table [dataSource]="dataSource" multiTemplateDataRows
               class="table-multishare-history-entry-list">
      <ng-container [matColumnDef]="column" *ngFor="let column of columnsToDisplay">
        <mat-header-cell *matHeaderCellDef> {{toTitleCase(column)}} </mat-header-cell>
        <mat-cell *matCellDef="let element">
          <ng-container [ngSwitch]="column">
            <ng-container *ngSwitchCase="'performedAt'">
              {{element.performedAt?.valueOf() * 1000 | date: 'dd/MM/yyyy HH:mm' }}
            </ng-container>
            <ng-container *ngSwitchDefault>
              {{ element[column] }}
            </ng-container>
          </ng-container>
        </mat-cell>
      </ng-container>

      <ng-container matColumnDef="expandedDetail">
        <mat-cell *matCellDef="let element" [attr.colspan]="columnsToDisplay.length">
          <div class="multishare-history-entry-detail"
               [@detailExpand]="element === expandedElement ? 'expanded' : 'collapsed'">
            <app-multishare-history-entry
              [shareResults]="element.shareResults"></app-multishare-history-entry>
          </div>
        </mat-cell>
      </ng-container>

      <mat-header-row *matHeaderRowDef="columnsToDisplay"></mat-header-row>
      <mat-row *matRowDef="let row; columns: columnsToDisplay;" class="multishare-history-entry-row"
               [class.multishare-history-entry-expanded]="expandedElement === row"
               (click)="expandedElement = expandedElement === row ? null : row"></mat-row>
      <mat-row *matRowDef="let row; columns: ['expandedDetail']"
               class="multishare-history-entry-detail-row"></mat-row>
    </mat-table>
  `,
  styleUrls: ['./multishare-history-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ]
})
export class MultishareHistoryTableComponent implements OnInit, OnDestroy {
  columnsToDisplay = ['id', 'state', 'type', 'performedAt', 'performedBy'];
  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  dataSource = new MatTableDataSource<Share>();
  filter$: BehaviorSubject<MultishareHistoryFilterModel> = new BehaviorSubject<MultishareHistoryFilterModel>({});
  readonly defaultPageIndex: number = 0;
  readonly defaultPageSize: number = 50;
  expandedElement: Share | undefined;
  private subSink = new SubSink();

  constructor(private facade: MultishareHistoryFacadeService) {
  }

  @Input()
  set searchFormValues(val: MultishareHistoryFilterModel) {
    if (val) {
      this.filter$.next(val);
    }
  }

  ngOnInit(): void {
    this.listenToPageSortFilterChanges();
    this.populateDataSource();
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  toTitleCase(text: string): string {
    const result = text.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
  }

  private listenToPageSortFilterChanges() {
    this.subSink.sink = merge(
      this.paginator.page,
      this.filter$.asObservable())
      .pipe(debounceTime(50))
      .subscribe(() => {
        this.loadMultishareHistoryEntries();
      });
  }

  private loadMultishareHistoryEntries() {
    this.facade.loadMultishareHistoryEntries(this.filter$.getValue(),
      {
        page: {
          page: this.paginator.pageIndex || this.defaultPageIndex,
          size: this.paginator.pageSize || this.defaultPageSize
        }
      } as Partial<MultishareHistoryFilter>);
  }

  private populateDataSource() {
    this.subSink.sink = this.facade.selectMultishareHistoryEntries().subscribe(multishareEntries => {
      this.dataSource.data = multishareEntries.content;
      this.paginator.length = multishareEntries.totalElements;
    });
  }

}
